/* global BigInt */
/**
 * Miroir exact de `backend/app/core/station_calc.py` pour les aperçus à l'écran.
 *
 * Le serveur reste la seule source des chiffres enregistrés ; ce fichier donne
 * le même résultat, au centilitre et au franc près, avant l'envoi (aperçu de
 * clôture, barème importé, tolérance). Mêmes fonctions, mêmes jeux de tests
 * (`stationCalc.test.js` ↔ `backend/app/tests/unit/test_station_calc.py`).
 *
 * Calcul en décimal exact (entiers `BigInt` et nombre de décimales), jamais en
 * virgule flottante : `0,1 + 0,2` ou `1 792,4 × 940` ne doivent pas dépendre du
 * binaire. Arrondi « la moitié s'éloigne de zéro », comme `ROUND_HALF_UP`.
 * Les fonctions rendent des nombres JavaScript déjà arrondis.
 */

export const CHART_OUT_OF_RANGE = 'Hauteur hors du barème de la cuve';
export const DEFAULT_VARIANCE_PERCENT = 0.5;
export const DEFAULT_VARIANCE_LITERS = 20;
export const DEFAULT_TIMEZONE = 'Africa/Bamako';
const AUTONOMY_WINDOW_DAYS = 7;
const AUTONOMY_FALLBACK_DAYS = 28;
const READING_RANK = { OPENING: 0, PRICE_CHANGE: 1, CLOSING: 2 };

/** Calcul impossible ; le message est une phrase française pour l'écran. */
export class StationCalcError extends Error {
  constructor(message) {
    super(message);
    this.name = 'StationCalcError';
  }
}

// --- Décimal exact ------------------------------------------------------------------

const DIX = BigInt(10);
const ZERO = BigInt(0);
const UN = BigInt(1);
const DEUX = BigInt(2);
// Boucle et non `**` : un transpileur réécrit `**` en `Math.pow`, qui refuse les BigInt.
const PUISSANCES = [UN];
const puissance = (n) => {
  while (PUISSANCES.length <= n) PUISSANCES.push(PUISSANCES[PUISSANCES.length - 1] * DIX);
  return PUISSANCES[n];
};

const NOMBRE = /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i;

/** `{ n, s }` : la valeur vaut n / 10^s. */
export const dec = (valeur, quoi = 'Nombre') => {
  if (valeur && typeof valeur === 'object' && typeof valeur.n === 'bigint') return valeur;
  if (valeur === null || valeur === undefined || valeur === '' || typeof valeur === 'boolean') {
    throw new StationCalcError(`${quoi} manquant.`);
  }
  if (typeof valeur === 'number' && !Number.isFinite(valeur)) {
    throw new StationCalcError(`${quoi} invalide : ${valeur}.`);
  }
  const texte = String(valeur).trim().replace(/[\s  ]/g, '').replace(',', '.');
  if (!NOMBRE.test(texte)) throw new StationCalcError(`${quoi} invalide : ${valeur}.`);
  const [mantisse, exposant] = texte.toLowerCase().split('e');
  const negatif = mantisse.startsWith('-');
  const corps = mantisse.replace(/^[-+]/, '');
  const [entier, fraction = ''] = corps.split('.');
  let n = BigInt(`${entier || '0'}${fraction}`);
  let s = fraction.length;
  const e = Number(exposant || 0);
  if (e > 0) {
    if (e >= s) {
      n *= puissance(e - s);
      s = 0;
    } else {
      s -= e;
    }
  } else if (e < 0) {
    s += -e;
  }
  return { n: negatif ? -n : n, s };
};

const aligner = (a, b) => {
  const s = Math.max(a.s, b.s);
  return [a.n * puissance(s - a.s), b.n * puissance(s - b.s), s];
};
const plus = (a, b) => {
  const [x, y, s] = aligner(a, b);
  return { n: x + y, s };
};
const moins = (a, b) => {
  const [x, y, s] = aligner(a, b);
  return { n: x - y, s };
};
const fois = (a, b) => ({ n: a.n * b.n, s: a.s + b.s });
const comparer = (a, b) => {
  const [x, y] = aligner(a, b);
  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
};
const absolu = (a) => ({ n: a.n < ZERO ? -a.n : a.n, s: a.s });
const estNegatif = (a) => a.n < ZERO;

/** Arrondi à `places` décimales, la moitié s'éloignant de zéro. */
const arrondir = (a, places) => {
  if (a.s <= places) return { n: a.n * puissance(places - a.s), s: places };
  const facteur = puissance(a.s - places);
  const negatif = a.n < ZERO;
  const valeur = negatif ? -a.n : a.n;
  let quotient = valeur / facteur;
  if ((valeur % facteur) * DEUX >= facteur) quotient += UN;
  return { n: negatif ? -quotient : quotient, s: places };
};

/** a ÷ b arrondi à `places` décimales (moitié vers le haut, en valeur absolue). */
const diviser = (a, b, places) => {
  if (b.n === ZERO) throw new StationCalcError('Division par zéro.');
  let numerateur = a.n * puissance(b.s + places);
  let denominateur = b.n * puissance(a.s);
  const negatif = (numerateur < ZERO) !== (denominateur < ZERO);
  if (numerateur < ZERO) numerateur = -numerateur;
  if (denominateur < ZERO) denominateur = -denominateur;
  let quotient = numerateur / denominateur;
  if ((numerateur % denominateur) * DEUX >= denominateur) quotient += UN;
  return { n: negatif ? -quotient : quotient, s: places };
};

/** Texte décimal exact (`"1792.40"`). */
export const decEnTexte = (a) => {
  const negatif = a.n < ZERO;
  let chiffres = (negatif ? -a.n : a.n).toString();
  if (a.s > 0) {
    chiffres = chiffres.padStart(a.s + 1, '0');
    chiffres = `${chiffres.slice(0, -a.s)}.${chiffres.slice(-a.s)}`;
  }
  return `${negatif && a.n !== ZERO ? '-' : ''}${chiffres}`;
};
const enNombre = (a) => Number(decEnTexte(a));

const litres = (a) => arrondir(a, 2);

// --- Dates ---------------------------------------------------------------------------

/** Instant à partir d'une date ou d'un texte ISO ; un texte sans fuseau est lu en UTC. */
export const versDate = (valeur) => {
  if (valeur instanceof Date) {
    if (Number.isNaN(valeur.getTime())) throw new StationCalcError('Date et heure invalides.');
    return valeur;
  }
  if (typeof valeur === 'string' && valeur.trim()) {
    const texte = valeur.trim();
    const avecFuseau = /([zZ]|[+-]\d{2}:?\d{2})$/.test(texte) || (!texte.includes('T') && texte.length <= 10);
    const date = new Date(avecFuseau ? texte : `${texte}Z`);
    if (Number.isNaN(date.getTime())) throw new StationCalcError(`Date et heure invalides : ${valeur}.`);
    return date;
  }
  throw new StationCalcError('Date et heure manquantes.');
};

// --- Température ------------------------------------------------------------------------

/** Volume ramené à 15 °C : volume × (1 − α × (T − 15)), au centilitre. Sans température : inchangé. */
export const to15c = (volume, temperatureC, coeff) => {
  const vol = dec(volume, 'Volume');
  if (temperatureC === null || temperatureC === undefined || temperatureC === '') return enNombre(vol);
  const temperature = dec(temperatureC, 'Température');
  const alpha = coeff === null || coeff === undefined || coeff === '' ? dec(0) : dec(coeff, 'Coefficient de dilatation');
  const facteur = moins(dec(1), fois(alpha, moins(temperature, dec(15))));
  return enNombre(litres(fois(vol, facteur)));
};

// --- Barème ------------------------------------------------------------------------------

/** Barème trié et contrôlé : `[[hauteur_cm, litres], …]` (nombres). */
export const normalizeChart = (points) => {
  const lignes = (points || []).map((point) => {
    let hauteur;
    let volume;
    if (Array.isArray(point)) {
      [hauteur, volume] = point;
    } else if (point && typeof point === 'object') {
      hauteur = point.height_cm ?? point.hauteur;
      volume = point.liters ?? point.litres;
    } else {
      throw new StationCalcError('Chaque ligne du barème donne une hauteur et un volume.');
    }
    return [dec(hauteur, 'Hauteur'), dec(volume, 'Volume')];
  });
  if (lignes.length < 2) throw new StationCalcError('Le barème doit compter au moins deux lignes.');
  lignes.sort((a, b) => comparer(a[0], b[0]));
  lignes.forEach(([hauteur, volume]) => {
    if (estNegatif(hauteur) || estNegatif(volume)) {
      throw new StationCalcError('Le barème ne peut pas contenir de hauteur ni de volume négatifs.');
    }
  });
  for (let i = 1; i < lignes.length; i += 1) {
    const [h0, v0] = lignes[i - 1];
    const [h1, v1] = lignes[i];
    if (comparer(h1, h0) === 0) {
      throw new StationCalcError(`Deux lignes du barème ont la même hauteur (${decEnTexte(h1)} cm).`);
    }
    if (comparer(v1, v0) < 0) {
      throw new StationCalcError(`Le volume du barème doit augmenter avec la hauteur (ligne ${decEnTexte(h1)} cm).`);
    }
  }
  return lignes.map(([hauteur, volume]) => [enNombre(hauteur), enNombre(volume)]);
};

/** Litres lus sur le barème pour une hauteur (interpolation linéaire). */
export const chartVolume = (points, heightCm) => {
  const lignes = normalizeChart(points).map(([h, v]) => [dec(h), dec(v)]);
  const hauteur = dec(heightCm, 'Hauteur');
  if (comparer(hauteur, lignes[0][0]) < 0 || comparer(hauteur, lignes[lignes.length - 1][0]) > 0) {
    throw new StationCalcError(CHART_OUT_OF_RANGE);
  }
  for (let i = 1; i < lignes.length; i += 1) {
    const [h0, v0] = lignes[i - 1];
    const [h1, v1] = lignes[i];
    if (comparer(hauteur, h0) >= 0 && comparer(hauteur, h1) <= 0) {
      if (comparer(hauteur, h0) === 0) return enNombre(litres(v0));
      if (comparer(hauteur, h1) === 0) return enNombre(litres(v1));
      // v0 + (h − h0) × (v1 − v0) / (h1 − h0), arrondi une seule fois.
      const pente = diviser(fois(moins(hauteur, h0), moins(v1, v0)), moins(h1, h0), 12);
      return enNombre(litres(plus(v0, pente)));
    }
  }
  throw new StationCalcError(CHART_OUT_OF_RANGE);
};

/**
 * Lit un barème collé ou importé : une ligne « hauteur;litres » par mesure
 * (virgule décimale acceptée, tabulation ou point-virgule comme séparateur).
 * Les lignes d'en-tête ou vides sont ignorées.
 */
export const parseChartCsv = (texte) => {
  const points = [];
  String(texte || '')
    .split(/\r?\n/)
    .forEach((ligne) => {
      const propre = ligne.trim();
      if (!propre) return;
      const morceaux = propre.split(/[;\t]/).map((m) => m.trim()).filter((m) => m !== '');
      if (morceaux.length < 2) return;
      const [hauteur, volume] = morceaux;
      if (!NOMBRE.test(hauteur.replace(/\s/g, '').replace(',', '.'))) return;
      if (!NOMBRE.test(volume.replace(/\s/g, '').replace(',', '.'))) return;
      points.push([enNombre(dec(hauteur)), enNombre(dec(volume))]);
    });
  return points;
};

// --- Pistolets -----------------------------------------------------------------------------

/** Litres d'un pistolet : fin − début − essais ; fin < début refusé sauf remise à zéro déclarée. */
export const nozzleLiters = (start, end, tests = 0, meterReset = false) => {
  const debut = dec(start, 'Index de début');
  const fin = dec(end, 'Index de fin');
  const essais = dec(tests === null || tests === undefined || tests === '' ? 0 : tests, 'Essais de pompe');
  if (estNegatif(debut) || estNegatif(fin)) throw new StationCalcError('Un index de pistolet ne peut pas être négatif.');
  if (estNegatif(essais)) throw new StationCalcError('Les essais de pompe ne peuvent pas être négatifs.');
  let sorti;
  if (meterReset) {
    sorti = fin;
  } else {
    if (comparer(fin, debut) < 0) {
      throw new StationCalcError(
        `L'index de fin (${decEnTexte(fin)}) est inférieur à l'index de début (${decEnTexte(debut)}) : `
          + 'corrigez la saisie, ou déclarez la remise à zéro du compteur avec son motif.',
      );
    }
    sorti = moins(fin, debut);
  }
  const vendu = moins(sorti, essais);
  if (estNegatif(vendu)) throw new StationCalcError('Les essais de pompe dépassent les litres sortis du pistolet.');
  return enNombre(litres(vendu));
};

const momentDuReleve = (releve) => {
  const valeur = releve.recorded_at || releve.read_at;
  return valeur ? versDate(valeur) : null;
};

/** Montant au franc (XOF) ou au centime. */
const montant = (valeur, devise = 'XOF') =>
  arrondir(valeur, ['XOF', 'XAF', 'JPY'].includes(String(devise).toUpperCase()) ? 0 : 2);

/**
 * Tranches de prix d'un poste, pistolet par pistolet : chaque tranche (d'un
 * relevé au suivant) au prix en vigueur à son début, montant arrondi au franc
 * par tranche. Mêmes entrées et mêmes sorties que la version serveur.
 */
export const priceTranches = (readings, prices, currency = 'XOF') => {
  const calendrier = (prices || [])
    .map((prix) => [versDate(prix.effective_at), prix])
    .sort((a, b) => a[0].getTime() - b[0].getTime());
  const parPistolet = new Map();
  (readings || []).forEach((releve, position) => {
    if (!releve.nozzle_id) throw new StationCalcError('Chaque relevé désigne son pistolet.');
    const cle = String(releve.nozzle_id);
    if (!parPistolet.has(cle)) parPistolet.set(cle, []);
    parPistolet.get(cle).push([position, releve]);
  });
  const tranches = [];
  parPistolet.forEach((lignes, nozzleId) => {
    const ordonnes = [...lignes].sort((a, b) => {
      const rangA = READING_RANK[String(a[1].kind || 'PRICE_CHANGE').toUpperCase()] ?? 1;
      const rangB = READING_RANK[String(b[1].kind || 'PRICE_CHANGE').toUpperCase()] ?? 1;
      if (rangA !== rangB) return rangA - rangB;
      const tA = momentDuReleve(a[1])?.getTime() ?? -Infinity;
      const tB = momentDuReleve(b[1])?.getTime() ?? -Infinity;
      if (tA !== tB) return tA - tB;
      return a[0] - b[0];
    });
    for (let i = 1; i < ordonnes.length; i += 1) {
      const premier = ordonnes[i - 1][1];
      const dernier = ordonnes[i][1];
      const debut = momentDuReleve(premier);
      if (!debut) throw new StationCalcError("L'heure du relevé de début de tranche manque.");
      const produit = premier.product_id || dernier.product_id || null;
      const quantite = nozzleLiters(premier.index, dernier.index, dernier.tests || 0, Boolean(dernier.meter_reset));
      let enVigueur = null;
      for (const [effet, prix] of calendrier) {
        if (effet.getTime() > debut.getTime()) break;
        if (!prix.product_id || !produit || String(prix.product_id) === String(produit)) enVigueur = [effet, prix];
      }
      if (!enVigueur) throw new StationCalcError('Aucun prix en vigueur pour ce pistolet au début de la tranche.');
      const prixUnitaire = dec(enVigueur[1].unit_price, 'Prix');
      const fin = momentDuReleve(dernier);
      tranches.push({
        nozzle_id: nozzleId,
        product_id: produit,
        start_index: enNombre(dec(premier.index)),
        end_index: enNombre(dec(dernier.index)),
        liters: quantite,
        unit_price: enNombre(prixUnitaire),
        amount: enNombre(montant(fois(dec(quantite), prixUnitaire), currency)),
        started_at: debut.toISOString(),
        ended_at: fin ? fin.toISOString() : null,
        price_effective_at: enVigueur[0].toISOString(),
      });
    }
  });
  return tranches;
};

// --- Caisse ----------------------------------------------------------------------------------

/** Espèces attendues = fond de caisse + ventes − ventes non payées en espèces − dépenses en espèces. */
export const expectedCash = (openingCash, salesAmount, nonCashTotal, cashExpenses, currency = 'XOF') => {
  const total = moins(
    moins(plus(dec(openingCash || 0, 'Fond de caisse'), dec(salesAmount || 0, 'Ventes')), dec(nonCashTotal || 0, 'Ventes hors espèces')),
    dec(cashExpenses || 0, 'Dépenses'),
  );
  return enNombre(montant(total, currency));
};

// --- Écarts ------------------------------------------------------------------------------------

/** Tolérance : le plus grand de `percent` % des litres et `liters` litres. */
export const tolerance = (baseLiters, percent = DEFAULT_VARIANCE_PERCENT, liters = DEFAULT_VARIANCE_LITERS) => {
  const base = absolu(dec(baseLiters, 'Litres'));
  const part = diviser(fois(base, dec(percent ?? DEFAULT_VARIANCE_PERCENT, 'Pourcentage')), dec(100), 12);
  const plancher = dec(liters ?? DEFAULT_VARIANCE_LITERS, 'Litres');
  return enNombre(litres(comparer(part, plancher) >= 0 ? part : plancher));
};

/** Vrai si |écart| dépasse la tolérance (réglages `variance_percent`, `variance_liters`). */
export const exceeds = (variance, baseLiters, settings = {}) => {
  const percent = settings.variance_percent ?? settings.percent ?? DEFAULT_VARIANCE_PERCENT;
  const plancher = settings.variance_liters ?? settings.liters ?? DEFAULT_VARIANCE_LITERS;
  const limite = dec(tolerance(baseLiters, percent, plancher));
  return comparer(absolu(dec(variance, 'Écart')), limite) > 0;
};

// --- Autonomie -------------------------------------------------------------------------------

const moyenneVendue = (ventes, fenetre) => {
  const vendus = ventes.slice(-fenetre).filter((v) => comparer(v, dec(0)) > 0);
  if (!vendus.length) return null;
  return [vendus.reduce((somme, v) => plus(somme, v), dec(0)), dec(vendus.length)];
};

/**
 * Jours de vente couverts par le stock : moyenne des jours vendus (> 0) parmi
 * les 7 derniers, sinon parmi les 28 derniers ; `null` sans aucun jour vendu.
 */
export const autonomyDays = (stock, dailySales) => {
  const ventes = (dailySales || [])
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => dec(v, 'Ventes'));
  const rythme = moyenneVendue(ventes, AUTONOMY_WINDOW_DAYS) || moyenneVendue(ventes, AUTONOMY_FALLBACK_DAYS);
  if (!rythme) return null;
  const disponible = dec(stock, 'Stock');
  if (comparer(disponible, dec(0)) <= 0) return 0;
  // stock ÷ (somme ÷ jours) = stock × jours ÷ somme : une seule division, un seul arrondi.
  const [somme, jours] = rythme;
  return enNombre(diviser(fois(disponible, jours), somme, 1));
};

// --- Journée -----------------------------------------------------------------------------------

/** Date locale (AAAA-MM-JJ) d'un instant dans le fuseau de la station. */
export const localBusinessDate = (momentUtc, tzName = DEFAULT_TIMEZONE) => {
  const date = versDate(momentUtc);
  let morceaux;
  try {
    morceaux = new Intl.DateTimeFormat('en-CA', {
      timeZone: tzName || DEFAULT_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
  } catch {
    throw new StationCalcError(`Fuseau horaire inconnu : ${tzName}.`);
  }
  const partie = (type) => morceaux.find((m) => m.type === type)?.value;
  return `${partie('year')}-${partie('month')}-${partie('day')}`;
};

// --- Cuves : réception et réconciliation (lot 031) ---------------------------------------------

const vide = (valeur) => valeur === null || valeur === undefined || valeur === '';
const optionnel = (valeur, quoi) => (vide(valeur) ? null : dec(valeur, quoi));

/** part ÷ base × 100, au centième ; `null` sans base (ou base nulle). */
export const percentOf = (part, base) => {
  const numerateur = optionnel(part, 'Écart');
  const denominateur = optionnel(base, 'Litres');
  if (!numerateur || !denominateur || denominateur.n === BigInt(0)) return null;
  return enNombre(diviser(fois(numerateur, dec(100)), denominateur, 2));
};

const seuils = (settings = {}) => [
  settings.variance_percent ?? DEFAULT_VARIANCE_PERCENT,
  settings.variance_liters ?? DEFAULT_VARIANCE_LITERS,
];

/**
 * Écarts d'une réception de camion, en litres à 15 °C (miroir de `reception_variances`).
 * Référence = chargé au dépôt, sinon bon de livraison ; transport = pigé à l'arrivée − référence ;
 * réception = reçu − pigé à l'arrivée (sans pige du camion : reçu − référence) ;
 * total = reçu − référence ; sans correction = volume lu reçu − référence.
 */
export const receptionVariances = ({
  delivered15c,
  received15c,
  loaded15c = null,
  truckArrival15c = null,
  receivedLiters = null,
  settings = {},
}) => {
  const livre = optionnel(delivered15c, 'Litres du bon de livraison');
  const charge = optionnel(loaded15c, 'Litres chargés');
  const arrivee = optionnel(truckArrival15c, 'Litres pigés dans le camion');
  const recu = dec(received15c, 'Litres reçus à 15 °C');
  const reference = charge || livre;
  if (!reference || comparer(reference, dec(0)) <= 0) {
    throw new StationCalcError('La quantité du bon de livraison manque.');
  }
  const transport = arrivee ? enNombre(litres(moins(arrivee, reference))) : null;
  const reception = enNombre(litres(moins(recu, arrivee || reference)));
  const total = litres(moins(recu, reference));
  const lu = optionnel(receivedLiters, 'Litres reçus');
  const [pourcent, plancher] = seuils(settings);
  const limite = tolerance(enNombre(reference), pourcent, plancher);
  return {
    reference_liters: enNombre(litres(reference)),
    transport_variance_liters: transport,
    reception_variance_liters: reception,
    total_variance_liters: enNombre(total),
    total_variance_percent: percentOf(decEnTexte(total), decEnTexte(reference)),
    uncorrected_variance_liters: lu ? enNombre(litres(moins(lu, reference))) : null,
    tolerance_liters: limite,
    exceeds: comparer(absolu(total), dec(limite)) > 0,
  };
};

/**
 * Réconciliation d'une cuve sur une journée, en litres à 15 °C (miroir de `tank_day`) :
 * attendu = ouverture + reçu − vendu ± ajustements ; écart = jaugé − attendu.
 */
export const tankDay = ({ opening15c, received15c = 0, sold15c = 0, adjustments = 0, closing15c, settings = {} }) => {
  const ouverture = optionnel(opening15c, "Stock d'ouverture");
  const recu = dec(vide(received15c) ? 0 : received15c, 'Reçu');
  const vendu = dec(vide(sold15c) ? 0 : sold15c, 'Vendu');
  const ajustements = dec(vide(adjustments) ? 0 : adjustments, 'Ajustements');
  const jauge = optionnel(closing15c, 'Stock jaugé');
  const attendu = ouverture ? litres(plus(moins(plus(ouverture, recu), vendu), ajustements)) : null;
  const ecart = attendu && jauge ? litres(moins(jauge, attendu)) : null;
  const [pourcent, plancher] = seuils(settings);
  const limite = tolerance(enNombre(vendu), pourcent, plancher);
  return {
    expected_15c: attendu ? enNombre(attendu) : null,
    variance_liters: ecart ? enNombre(ecart) : null,
    variance_percent: ecart ? percentOf(decEnTexte(ecart), decEnTexte(vendu)) : null,
    tolerance_liters: limite,
    exceeds: Boolean(ecart) && comparer(absolu(ecart), dec(limite)) > 0,
  };
};

/** Écart cumulé : Σ écarts ÷ Σ litres vendus des jours réconciliés (miroir de `cumulative_variance`). */
export const cumulativeVariance = (jours) => {
  let ecarts = dec(0);
  let ventes = dec(0);
  let compte = 0;
  (jours || []).forEach((jour) => {
    if (vide(jour?.variance_liters)) return;
    ecarts = plus(ecarts, dec(jour.variance_liters, 'Écart'));
    ventes = plus(ventes, dec(vide(jour.sold_15c) ? 0 : jour.sold_15c, 'Vendu'));
    compte += 1;
  });
  return {
    variance_liters: enNombre(litres(ecarts)),
    sold_15c: enNombre(litres(ventes)),
    days: compte,
    percent: compte ? percentOf(decEnTexte(ecarts), decEnTexte(ventes)) : null,
  };
};

// Mêmes noms que `backend/app/core/station_calc.py`, pour qui lit les deux côtés.
export {
  to15c as to_15c,
  chartVolume as chart_volume,
  normalizeChart as normalize_chart,
  nozzleLiters as nozzle_liters,
  priceTranches as price_tranches,
  expectedCash as expected_cash,
  autonomyDays as autonomy_days,
  localBusinessDate as local_business_date,
  receptionVariances as reception_variances,
  tankDay as tank_day,
  cumulativeVariance as cumulative_variance,
  percentOf as percent_of,
};
